import {
  ClearBehaviour,
  ConfirmationBox,
  GroupBox,
  Labeled,
  Select,
} from '@infotech-soe/erp-ui-kit';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { SelectFormControlEntryType } from '../../../../services/form/view-model/FormControlViewModel';
import { useStyles as useDialogStyles } from '../../dialogs/styles';

export enum MainTableMode {
  SELECTED = 'selected',
  ALL = 'all',
}

export enum SecondaryMode {
  NONE = 'none',
  TOGETHER = 'together',
  TOGETHER_ALL = 'together_all',
  SEPARATE = 'separate',
  SEPARATE_ALL = 'separate_all',
}

export interface ExcelExportOptions {
  mainTable: MainTableMode;
  secondary: SecondaryMode;
}

interface Props {
  open: boolean;
  hasSecondary: boolean;
  hasMultipleSecondary: boolean;
  onConfirm: (options: ExcelExportOptions) => void;
  onCancel: () => void;
}

const messages = defineMessages({
  title: {
    id: 'core.category.grid.excelExport.title',
    defaultMessage: 'Export to Excel',
  },
  mainTableLabel: {
    id: 'core.category.grid.excelExport.mainTable',
    defaultMessage: 'Main table',
  },
  secondaryLabel: {
    id: 'core.category.grid.excelExport.secondary',
    defaultMessage: 'Secondary table',
  },
  mainSelected: {
    id: 'core.category.grid.excelExport.mainSelected',
    defaultMessage: 'Selected rows',
  },
  mainAll: {
    id: 'core.category.grid.excelExport.mainAll',
    defaultMessage: 'All rows',
  },
  secondaryNone: {
    id: 'core.category.grid.excelExport.secondaryNone',
    defaultMessage: 'Do not export',
  },
  secondaryTogether: {
    id: 'core.category.grid.excelExport.secondaryTogether',
    defaultMessage: 'Export together',
  },
  secondarySeparate: {
    id: 'core.category.grid.excelExport.secondarySeparate',
    defaultMessage: 'Export separately',
  },
  secondaryTogetherAll: {
    id: 'core.category.grid.excelExport.secondaryTogetherAll',
    defaultMessage: 'Export together (all)',
  },
  secondarySeparateAll: {
    id: 'core.category.grid.excelExport.secondarySeparateAll',
    defaultMessage: 'Export separately (all)',
  },
});

const ExcelExportDialog: React.FC<Props> = ({
  open,
  hasSecondary,
  hasMultipleSecondary,
  onConfirm,
  onCancel,
}) => {
  const dialogClasses = useDialogStyles();
  const { formatMessage } = useIntl();

  const [mainTable, setMainTable] = useState<MainTableMode>(
    MainTableMode.SELECTED
  );
  const [secondary, setSecondary] = useState<SecondaryMode>(SecondaryMode.NONE);

  useEffect(() => {
    if (!hasSecondary) {
      setSecondary(SecondaryMode.NONE);
    }
  }, [hasSecondary]);

  const mainTableOptions = [
    {
      type: SelectFormControlEntryType.OPTION,
      value: MainTableMode.SELECTED,
      label: formatMessage(messages.mainSelected),
    },
    {
      type: SelectFormControlEntryType.OPTION,
      value: MainTableMode.ALL,
      label: formatMessage(messages.mainAll),
    },
  ];

  const secondaryOptions = [
    {
      type: SelectFormControlEntryType.OPTION,
      value: SecondaryMode.NONE,
      label: formatMessage(messages.secondaryNone),
    },
    {
      type: SelectFormControlEntryType.OPTION,
      value: SecondaryMode.TOGETHER,
      label: formatMessage(messages.secondaryTogether),
    },
    ...(hasMultipleSecondary
      ? [
          {
            type: SelectFormControlEntryType.OPTION,
            value: SecondaryMode.TOGETHER_ALL,
            label: formatMessage(messages.secondaryTogetherAll),
          },
        ]
      : []),
    {
      type: SelectFormControlEntryType.OPTION,
      value: SecondaryMode.SEPARATE,
      label: formatMessage(messages.secondarySeparate),
    },
    ...(hasMultipleSecondary
      ? [
          {
            type: SelectFormControlEntryType.OPTION,
            value: SecondaryMode.SEPARATE_ALL,
            label: formatMessage(messages.secondarySeparateAll),
          },
        ]
      : []),
  ];

  if (!open) {
    return null;
  }

  return (
    <ConfirmationBox
      className={dialogClasses.dialog}
      open={open}
      title={formatMessage(messages.title)}
      onConfirm={() => onConfirm({ mainTable, secondary })}
      onCancel={onCancel}
      onClose={onCancel}
    >
      <GroupBox>
        <Labeled label={formatMessage(messages.mainTableLabel)}>
          <Select
            required
            withClear={ClearBehaviour.NONE}
            options={mainTableOptions}
            value={mainTable}
            onChange={v => v !== null && setMainTable(v)}
          />
        </Labeled>
        <Labeled label={formatMessage(messages.secondaryLabel)}>
          <Select
            required
            disabled={!hasSecondary}
            withClear={ClearBehaviour.NONE}
            options={secondaryOptions}
            value={secondary}
            onChange={v => v !== null && setSecondary(v)}
          />
        </Labeled>
      </GroupBox>
    </ConfirmationBox>
  );
};

export default ExcelExportDialog;
